// Вкладка «Документы». Номер документа наружу не уходит вообще: сервер отдаёт только
// последние четыре знака (server/app/serializers/employees.py). Это не урезанный показ, а
// весь объём данных, который вообще существует на этой стороне — сверить документ глазами
// по четырём знакам можно, восстановить номер нельзя.
//
// Роли без права на документы сервер отвечает отказом, а не пустым списком: пустой список
// читался бы как «документов нет», и человек пошёл бы их заводить заново. Поэтому отказ
// показывается состоянием ошибки с причиной, как есть.

import { RiAddLine, RiFileList3Line } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { countLabel } from '@/shared/text/plural';
import type { DocumentOut, EmployeeOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { docTypeTitle } from '../dictionaries';
import { useDocumentsQuery } from '../queries';
import type { PeopleRoute } from '../route';
import { PANEL_DOCUMENT, openPanelHref } from '../route';

const EDIT_PERMISSION = 'core.employee.edit';

export function DocumentsTab({ employee, route }: { employee: EmployeeOut; route: PeopleRoute }) {
  const navigate = useNavigate();
  const query = useDocumentsQuery(employee.id);
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);

  const addButton = (
    <Button.Root
      type='button'
      variant='primary'
      mode='stroke'
      size='xsmall'
      onClick={() => navigate(openPanelHref(route, PANEL_DOCUMENT, employee.id))}
    >
      <Button.Icon as={RiAddLine} />
      Добавить документ
    </Button.Root>
  );

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={query.data}
        error={query.error}
        subject='документы человека'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3'>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiFileList3Line}
            title='Документов не заведено'
            description='Ни паспорта, ни СНИЛС, ни удостоверения охранника. Без действующего удостоверения человека не поставить на пост, где нужен разряд.'
            action={canEdit ? addButton : undefined}
          />
        }
      >
        {(data) => (
          <>
            <div className='flex items-center justify-between gap-2'>
              <span className='text-paragraph-sm text-text-sub-600'>
                {countLabel(data.items.length, ['документ', 'документа', 'документов'])}. Показаны
                последние четыре знака номера — полный номер система наружу не отдаёт.
              </span>
              {canEdit && addButton}
            </div>
            <div className='flex flex-col gap-3'>
              {data.items.map((document) => (
                <DocumentCard key={document.id} document={document} />
              ))}
            </div>
          </>
        )}
      </ScreenState>
    </div>
  );
}

function DocumentCard({ document }: { document: DocumentOut }) {
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        <span className='text-label-sm text-text-strong-950'>
          {docTypeTitle(document.doc_type)}
        </span>
        {!document.is_active && (
          <Badge.Root variant='lighter' color='gray' size='medium'>
            недействующий
          </Badge.Root>
        )}
        {expiredWords(document) !== null && (
          <Badge.Root variant='lighter' color='red' size='medium'>
            {expiredWords(document)}
          </Badge.Root>
        )}
      </div>
      <FieldList>
        <FieldRow
          label='Номер'
          value={document.number_last4 === null ? null : `…${document.number_last4}`}
          missing='номер не заведён: сверить документ глазами не по чему'
        />
        <FieldRow label='Кем выдан' value={document.issued_by} missing='не заполнено' />
        <FieldRow
          label='Дата выдачи'
          value={document.issued_at === null ? null : formatDateOnly(document.issued_at)}
          missing='не заполнена'
        />
        <FieldRow
          label='Действует до'
          value={document.expires_at === null ? null : formatDateOnly(document.expires_at)}
          missing='срок не указан: система не предупредит, когда документ истечёт'
        />
        {document.guard_category !== null && (
          <FieldRow
            label='Разряд'
            value={`${document.guard_category} разряд`}
            note='разряд из удостоверения ограничивает, на какие посты человека можно ставить'
          />
        )}
      </FieldList>
    </div>
  );
}

/** Истёк ли документ. Срок в прошлом — это факт, а не оформление: он закрывает посты. */
function expiredWords(document: DocumentOut): string | null {
  if (document.expires_at === null) {
    return null;
  }
  const today = new Date().toISOString().slice(0, 10);
  return document.expires_at < today ? 'срок истёк' : null;
}
